<template>
  <div class="row product-import-detail">
    <div class="col-md-12">
      <div class="settings-top-bar sticky-header">
        <div class="top-bar">
          <p>
            <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="goBack">
              <i class="ti-arrow-left"></i>
              {{ $t('general.back') }}
            </a>
          </p>
          <p class="d-inline-flex">
            <a
              v-if="batch && batch.status === 'PENDING'"
              class="btn delete mar-rt-10"
              :title="$t('general.cancel')"
              @click.prevent="cancelBatch"
            >
              <img src="/static/img/icons/icon-close.png" alt="">
              {{ $t('general.cancel') }}
            </a>
            <button
              v-if="canConfirm"
              type="button"
              class="btn btn-success btn-icon save"
              :disabled="confirming"
              :title="$t('productImport.confirm')"
              @click.prevent="openConfirmModal"
            >
              <img src="/static/img/icons/save.png" alt="">
              {{ $t('productImport.confirm') }}
            </button>
          </p>
        </div>
      </div>

      <div class="card mar-to-15">
        <div class="card-content">
          <div class="import-detail-header">
            <div class="import-detail-header__title">
              <h5>{{ $t('productImport.import') }} #{{ batch && batch.id }}</h5>
              <span v-if="batch" class="import-status" :class="'import-status--' + (batch.status || '').toLowerCase()">
                {{ statusLabel(batch.status) }}
              </span>
            </div>
            <p v-if="batch" class="import-detail-meta text-muted">
              {{ batch.created_at }}
              <span v-if="batch.original_filename"> · {{ batch.original_filename }}</span>
              <span> · {{ batch.total_rows }} {{ $t('productImport.products') }}</span>
              <span v-if="priceProfileLabel"> · {{ $t('productImport.selectPriceProfile') }}: {{ priceProfileLabel }}</span>
            </p>
            <p v-if="batch" class="import-detail-stats text-muted">
              {{ $t('productImport.pending') }}: {{ batch.pending_rows }}
              · {{ $t('productImport.confirmed') }}: {{ batch.confirmed_rows }}
              · {{ $t('productImport.removed') }}: {{ batch.removed_rows }}
              · {{ $t('productImport.errors') }}: {{ batch.error_rows }}
            </p>
          </div>
          <hr class="sm">

          <div :class="loading ? 'show-loader' : 'hide-loader'">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>

          <div class="product-import-table data-table mar-bt-20">
            <el-table :data="rows" style="width:100%">
              <el-table-column :label="$t('productImport.product')" min-width="160">
                <template slot-scope="scope">
                  {{ (scope.row.data && scope.row.data.name) || ('#' + (scope.row.product_id || scope.row.row_number)) }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('productImport.actionType')" width="150">
                <template slot-scope="scope">{{ actionLabel(scope.row.action) }}</template>
              </el-table-column>
              <el-table-column :label="$t('productImport.price')" width="80">
                <template slot-scope="scope">
                  {{ scope.row.data && scope.row.data.price !== '' ? scope.row.data.price : '-' }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('productImport.status')" width="130">
                <template slot-scope="scope">{{ rowStatusLabel(scope.row.status) }}</template>
              </el-table-column>
              <el-table-column :label="$t('productImport.errors')" width="160">
                <template slot-scope="scope">
                  <span v-if="scope.row.validation_errors && scope.row.validation_errors.length" style="color:#c0392b;">
                    {{ scope.row.validation_errors.join('; ') }}
                  </span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('general.actions')" width="300">
                <template slot-scope="scope">
                  <div class="import-row-actions">
                    <a
                      v-if="canEditRow(scope.row)"
                      class="btn delete mar-rt-10"
                      :title="$t('productImport.remove')"
                      @click.prevent="removeRow(scope.row)"
                    >
                      <img src="/static/img/icons/icon-close.png" alt="">
                      {{ $t('productImport.remove') }}
                    </a>
                    <div
                      v-if="canEditRow(scope.row)"
                      class="edit import-row-edit"
                      @click.stop="openEdit(scope.row)"
                    >
                      <i class="fa fa-pencil"></i>{{ $t('general.edit') }}
                    </div>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>

      <el-dialog
        :title="$t('productImport.confirmTitle')"
        :visible.sync="confirmVisible"
        width="440px"
        custom-class="product-import-edit-dialog"
        append-to-body
        :modal-append-to-body="true"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
      >
        <div class="form-group">
          <label class="pad-bt-5">{{ $t('productImport.selectPriceProfile') }} *</label>
          <el-select
            v-model="confirmPriceProfileId"
            filterable
            :placeholder="$t('productImport.selectPriceProfile')"
            style="width:100%;"
          >
            <el-option
              v-for="profile in locationPriceProfiles"
              :key="profile.id"
              :label="profile.name"
              :value="profile.id"
            />
          </el-select>
          <p class="text-muted" style="margin:8px 0 0;font-size:12px;">{{ $t('productImport.confirmPriceHint') }}</p>
        </div>
        <span slot="footer" class="dialog-footer">
          <button
            type="button"
            class="btn btn-info product-import-dialog-btn"
            @click="confirmVisible = false"
          >{{ $t('general.cancel') }}</button>
          <button
            type="button"
            class="btn btn-success product-import-dialog-btn"
            :disabled="confirming || !confirmPriceProfileId"
            @click="confirmBatch"
          >{{ $t('productImport.confirm') }}</button>
        </span>
      </el-dialog>

      <el-dialog
        :title="$t('productImport.editRow')"
        :visible.sync="editVisible"
        width="92%"
        top="4vh"
        custom-class="product-import-edit-dialog"
        append-to-body
        :modal-append-to-body="true"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
      >
        <div v-if="editData" class="product-import-edit-body">
          <h6>{{ $t('productImport.sectionIdentity') }}</h6>
          <hr class="sm">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>PRODUCT ID</label>
                <el-input
                  v-model="editData.product_id"
                  clearable
                  @blur="validateProductId"
                  @clear="onProductIdCleared"
                />
                <p class="product-match-status" :class="productMatchClass">{{ productMatchMessage }}</p>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>{{ $t('productImport.searchProduct') }}</label>
                <el-select
                  v-model="selectedSearchProductId"
                  filterable
                  remote
                  clearable
                  reserve-keyword
                  :remote-method="searchProducts"
                  :loading="searchProductsLoading"
                  :placeholder="$t('productImport.searchProductPlaceholder')"
                  style="width:100%;"
                  @change="onSearchProductSelected"
                >
                  <el-option
                    v-for="prod in productSearchResults"
                    :key="prod.id"
                    :value="prod.id"
                    :label="'#' + prod.id + ' — ' + prod.name"
                  />
                </el-select>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group">
                <label>{{ $t('productImport.product') }}</label>
                <el-input v-model="editData.name"/>
              </div>
            </div>
          </div>

          <h6>{{ $t('productImport.sectionCategories') }}</h6>
          <hr class="sm">
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label>{{ $t('productImport.topCategory') }}</label>
                <el-select
                  v-model="editData.topCateg"
                  filterable
                  allow-create
                  default-first-option
                  clearable
                  :placeholder="$t('general.add')"
                  style="width:100%;"
                >
                  <el-option
                    v-for="name in topCategoryNames"
                    :key="'tc-' + name"
                    :label="name"
                    :value="name"
                  />
                </el-select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label>{{ $t('productImport.category') }}</label>
                <el-select
                  v-model="editData.categ"
                  filterable
                  allow-create
                  default-first-option
                  clearable
                  :placeholder="$t('general.add')"
                  style="width:100%;"
                >
                  <el-option
                    v-for="name in categoryNames"
                    :key="'c-' + name"
                    :label="name"
                    :value="name"
                  />
                </el-select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label>{{ $t('productImport.secCategory') }}</label>
                <el-select
                  v-model="secCategList"
                  multiple
                  filterable
                  allow-create
                  default-first-option
                  clearable
                  :placeholder="$t('general.add')"
                  style="width:100%;"
                >
                  <el-option
                    v-for="name in categoryNames"
                    :key="'sc-' + name"
                    :label="name"
                    :value="name"
                  />
                </el-select>
              </div>
            </div>
          </div>

          <h6>{{ $t('productImport.sectionBase') }}</h6>
          <hr class="sm">
          <div class="row">
            <div class="col-md-3">
              <div class="form-group">
                <label>{{ $t('productImport.price') }}</label>
                <el-input v-model="editData.price"/>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>{{ $t('productImport.active') }}</label>
                <el-input v-model="editData.active" :placeholder="$t('productImport.activeHint')"/>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>{{ $t('productImport.posId') }}</label>
                <el-input v-model="editData.pos_id"/>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>{{ $t('productImport.vat') }}</label>
                <el-input v-model="editData.vat"/>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>{{ $t('productImport.section') }}</label>
                <el-input v-model="editData.section"/>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>{{ $t('productImport.weight') }}</label>
                <el-input v-model="editData.weight"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>{{ $t('productImport.imageFilename') }}</label>
                <el-input v-model="editData.image" :placeholder="$t('productImport.imageFilenameHint')"/>
              </div>
            </div>
          </div>

          <h6>{{ $t('productImport.sectionDescription') }}</h6>
          <hr class="sm">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>{{ $t('productImport.desc') }}</label>
                <el-input v-model="editData.desc" type="textarea" :rows="3"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>{{ $t('productImport.nutritional') }}</label>
                <el-input v-model="editData.nutritional" type="textarea" :rows="3"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>{{ $t('productImport.allergenic') }}</label>
                <el-input v-model="editData.allergenic" type="textarea" :rows="2"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>{{ $t('productImport.ingredients') }}</label>
                <el-input v-model="editData.ingredients" type="textarea" :rows="2"/>
              </div>
            </div>
          </div>

          <h6>{{ $t('productImport.sectionPrices') }}</h6>
          <hr class="sm">
          <div
            v-for="group in channelPriceGroups"
            :key="group.key"
            class="price-source-group"
          >
            <div class="price-source-group__title">{{ group.label }}</div>
            <div class="row">
              <div
                v-for="field in group.fields"
                :key="field.key"
                class="col-md-3"
              >
                <div class="form-group">
                  <label>{{ field.label }}</label>
                  <el-input v-model="editData[field.key]"/>
                </div>
              </div>
            </div>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <button
            type="button"
            class="btn btn-info product-import-dialog-btn"
            @click="editVisible = false"
          >{{ $t('general.cancel') }}</button>
          <button
            type="button"
            class="btn btn-success product-import-dialog-btn"
            :disabled="saving || productIdInvalid"
            @click="saveEdit"
          >{{ $t('general.save') }}</button>
        </span>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2'
import { Table, TableColumn, Dialog, Input, Select, Option } from 'element-ui'
import Vue from 'vue'
Vue.use(Select)

export default {
  name: 'ProductImportDetail',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dialog.name]: Dialog,
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option
  },
  props: {
    route: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      confirming: false,
      saving: false,
      batch: null,
      rows: [],
      editVisible: false,
      confirmVisible: false,
      editRowId: null,
      editData: null,
      locationId: null,
      importId: null,
      locationPriceProfiles: [],
      confirmPriceProfileId: null,
      topCategoryNames: [],
      categoryNames: [],
      secCategList: [],
      productSearchResults: [],
      searchProductsLoading: false,
      searchTimeout: null,
      selectedSearchProductId: null,
      productMatchState: 'idle',
      productMatchName: '',
      validatingProductId: false
    }
  },
  computed: {
    canConfirm () {
      if (!this.batch) return false
      return ['PENDING', 'PARTIALLY_CONFIRMED'].indexOf(this.batch.status) !== -1 &&
        this.batch.pending_rows > 0
    },
    priceProfileLabel () {
      if (!this.batch) return ''
      if (this.batch.price_profile && this.batch.price_profile.name) {
        return this.batch.price_profile.name
      }
      if (this.batch.price_profiles_id) {
        return '#' + this.batch.price_profiles_id
      }
      return ''
    },
    productIdInvalid () {
      return this.productMatchState === 'unknown'
    },
    productMatchMessage () {
      if (this.productMatchState === 'new') {
        return this.$t('productImport.productMatchNew')
      }
      if (this.productMatchState === 'found') {
        return this.$t('productImport.productMatchFound', { name: this.productMatchName })
      }
      if (this.productMatchState === 'unknown') {
        return this.$t('productImport.productMatchUnknown')
      }
      return ''
    },
    productMatchClass () {
      return {
        'is-muted': this.productMatchState === 'new' || this.productMatchState === 'idle',
        'is-success': this.productMatchState === 'found',
        'is-error': this.productMatchState === 'unknown'
      }
    },
    channelPriceGroups () {
      return [
        {
          key: 'glovo',
          label: 'GLOVO',
          fields: [{ key: 'price_glovo_eatin', label: 'PREȚ GLOVO EatIn' }]
        },
        {
          key: 'bolt',
          label: 'BOLT',
          fields: [{ key: 'price_bolt_eatin', label: 'PREȚ BOLT EatIn' }]
        },
        {
          key: 'wolt',
          label: 'Wolt',
          fields: [{ key: 'price_wolt_eatin', label: 'PREȚ Wolt EatIn' }]
        },
        {
          key: 'website',
          label: 'Website',
          fields: [
            { key: 'price_website_eatin', label: 'PREȚ Website EatIn' },
            { key: 'price_website_togo', label: 'PREȚ Website ToGo' },
            { key: 'price_website_delivery', label: 'PREȚ Website Delivery' }
          ]
        },
        {
          key: 'qr',
          label: 'QR',
          fields: [{ key: 'price_qr_eatin', label: 'PREȚ QR EatIn' }]
        },
        {
          key: 'kiosk',
          label: 'Kiosk',
          fields: [
            { key: 'price_kiosk_eatin', label: 'PREȚ Kiosk EatIn' },
            { key: 'price_kiosk_togo', label: 'PREȚ Kiosk ToGo' }
          ]
        },
        {
          key: 'pos',
          label: 'POS',
          fields: [
            { key: 'price_pos_eatin', label: 'PREȚ POS EatIn' },
            { key: 'price_pos_togo', label: 'PREȚ POS ToGo' },
            { key: 'price_pos_delivery', label: 'PREȚ POS Delivery' }
          ]
        },
        {
          key: 'app',
          label: 'App',
          fields: [
            { key: 'price_app_eatin', label: 'PREȚ App EatIn' },
            { key: 'price_app_togo', label: 'PREȚ App ToGo' },
            { key: 'price_app_delivery', label: 'PREȚ App Delivery' }
          ]
        }
      ]
    }
  },
  watch: {
    editVisible (val) {
      this.syncDialogMaskClass()
      if (val) {
        this.loadCategoryOptions()
      }
    },
    confirmVisible () {
      this.syncDialogMaskClass()
    },
    secCategList (list) {
      if (!this.editData) return
      this.editData.secCateg = Array.isArray(list) ? list.filter(Boolean).join(', ') : ''
    }
  },
  created () {
    const params = (this.route && this.route.params) || this.$route.params
    this.locationId = params.locationId
    this.importId = params.importId
    this.loadLocationPriceProfiles()
    this.reload()
  },
  beforeDestroy () {
    document.body.classList.remove('tt-import-dialog-open')
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    syncDialogMaskClass () {
      const open = this.editVisible || this.confirmVisible
      document.body.classList.toggle('tt-import-dialog-open', open)
    },
    statusLabel (status) {
      const map = {
        PENDING: this.$t('productImport.statusPending'),
        CONFIRMED: this.$t('productImport.statusSuccessful'),
        PARTIALLY_CONFIRMED: this.$t('productImport.statusPartiallySuccessful'),
        CANCELED: this.$t('productImport.statusCanceled')
      }
      return map[status] || status
    },
    rowStatusLabel (status) {
      const map = {
        PENDING: this.$t('productImport.statusPending'),
        ERROR: this.$t('productImport.statusError'),
        REMOVED: this.$t('productImport.statusRemoved'),
        CONFIRMED: this.$t('productImport.statusSuccessful')
      }
      return map[status] || status
    },
    actionLabel (action) {
      const map = {
        CREATE: this.$t('productImport.actionCreate'),
        UPDATE: this.$t('productImport.actionUpdate'),
        PRICE_LIST_UPDATE: this.$t('productImport.actionPriceListUpdate')
      }
      return map[action] || action
    },
    canEditRow (row) {
      return row.status === 'PENDING' || row.status === 'ERROR'
    },
    goBack () {
      this.$router.push(`/locations/${this.locationId}/product-imports`)
    },
    loadLocationPriceProfiles () {
      this.apiRequest('/price-profiles', 'GET', {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        perPage: 200,
        page: 1
      }, (response) => {
        if (!response || !response.status) {
          this.locationPriceProfiles = []
          return
        }
        const items = response.data || []
        const locationTrueId = this.locationId
        this.locationPriceProfiles = items.filter((profile) => {
          const locs = profile.location_details || profile.locationDetails || []
          return locs.some((loc) => {
            const locId = loc.true_id != null ? loc.true_id : loc.id
            return String(locId) === String(locationTrueId)
          })
        })
      })
    },
    loadCategoryOptions () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/topcategory', 'GET', data, (response) => {
        if (response && response.status) {
          const rows = Array.isArray(response.data) ? response.data : []
          this.topCategoryNames = rows
            .map((row) => row.name)
            .filter((name) => !!name)
        }
      })
      this.apiRequest('/sorted-category', 'GET', data, (response) => {
        if (response && response.status) {
          const rows = Array.isArray(response.data) ? response.data : []
          this.categoryNames = rows
            .map((row) => row.name)
            .filter((name) => !!name)
        }
      })
    },
    reload () {
      this.loading = true
      this.apiRequest(`/product-imports/${this.importId}`, 'GET', {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }, (response) => {
        if (response && response.status) {
          this.batch = response.data
          this.confirmPriceProfileId = this.batch.price_profiles_id
        }
        this.apiRequest(`/product-imports/${this.importId}/rows`, 'GET', {
          token: this.$session.get('token')
        }, (rowsResponse) => {
          this.loading = false
          if (rowsResponse && rowsResponse.status) {
            this.rows = rowsResponse.data || []
          }
        })
      })
    },
    openConfirmModal () {
      this.confirmPriceProfileId = (this.batch && this.batch.price_profiles_id) || null
      this.confirmVisible = true
    },
    emptyEditData () {
      return {
        product_id: '',
        name: '',
        topCateg: '',
        categ: '',
        secCateg: '',
        price: '',
        desc: '',
        nutritional: '',
        allergenic: '',
        active: '',
        pos_id: '',
        image: '',
        weight: '',
        ingredients: '',
        vat: '',
        section: '',
        price_glovo_eatin: '',
        price_wolt_eatin: '',
        price_bolt_eatin: '',
        price_website_eatin: '',
        price_website_togo: '',
        price_website_delivery: '',
        price_qr_eatin: '',
        price_kiosk_eatin: '',
        price_kiosk_togo: '',
        price_pos_eatin: '',
        price_pos_togo: '',
        price_pos_delivery: '',
        price_app_eatin: '',
        price_app_togo: '',
        price_app_delivery: ''
      }
    },
    openEdit (row) {
      this.editRowId = row.id
      this.editData = Object.assign(this.emptyEditData(), row.data || {})
      const sec = (this.editData.secCateg || '').toString().trim()
      this.secCategList = sec
        ? sec.split(',').map((s) => s.trim()).filter(Boolean)
        : []
      this.selectedSearchProductId = null
      this.productSearchResults = []
      this.productMatchState = 'idle'
      this.productMatchName = ''
      this.editVisible = true
      this.$nextTick(() => {
        this.validateProductId()
      })
    },
    onProductIdCleared () {
      this.selectedSearchProductId = null
      this.productMatchState = 'new'
      this.productMatchName = ''
    },
    searchProducts (query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchProducts(query)
      }, 400)
    },
    fetchProducts (searchText) {
      this.searchProductsLoading = true
      const selectedIds = []
      if (this.editData && this.editData.product_id) {
        selectedIds.push(this.editData.product_id)
      }
      this.apiRequest('/get-products-for-options', 'GET', {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        search: searchText || '',
        selected_ids: selectedIds
      }, (response) => {
        this.searchProductsLoading = false
        if (response && response.status) {
          const products = (response.data && response.data.products) || []
          const selected = (response.data && response.data.selected_products) || []
          const byId = {}
          products.concat(selected).forEach((p) => {
            if (p && p.id != null) {
              byId[p.id] = p
            }
          })
          this.productSearchResults = Object.keys(byId).map((id) => byId[id])
        }
      })
    },
    onSearchProductSelected (productId) {
      if (!productId || !this.editData) {
        return
      }
      const found = this.productSearchResults.find((p) => String(p.id) === String(productId))
      this.editData.product_id = String(productId)
      if (found) {
        if (!this.editData.name) {
          this.editData.name = found.name
        }
        this.productMatchState = 'found'
        this.productMatchName = found.name
      } else {
        this.validateProductId()
      }
    },
    validateProductId () {
      if (!this.editData) {
        return
      }
      const raw = (this.editData.product_id == null ? '' : String(this.editData.product_id)).trim()
      this.editData.product_id = raw
      if (!raw) {
        this.productMatchState = 'new'
        this.productMatchName = ''
        this.selectedSearchProductId = null
        return
      }
      if (!/^\d+$/.test(raw)) {
        this.productMatchState = 'unknown'
        this.productMatchName = ''
        return
      }
      this.validatingProductId = true
      this.apiRequest('/get-products-for-options', 'GET', {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        search: raw,
        selected_ids: [raw]
      }, (response) => {
        this.validatingProductId = false
        if (!response || !response.status) {
          this.productMatchState = 'unknown'
          this.productMatchName = ''
          return
        }
        const products = (response.data && response.data.products) || []
        const selected = (response.data && response.data.selected_products) || []
        const all = products.concat(selected)
        const match = all.find((p) => String(p.id) === String(raw))
        if (match) {
          this.productMatchState = 'found'
          this.productMatchName = match.name
          this.selectedSearchProductId = match.id
          if (!this.productSearchResults.some((p) => String(p.id) === String(match.id))) {
            this.productSearchResults = [match].concat(this.productSearchResults)
          }
        } else {
          this.productMatchState = 'unknown'
          this.productMatchName = ''
        }
      })
    },
    saveEdit () {
      if (this.productIdInvalid) {
        return
      }
      if (this.editData) {
        this.editData.secCateg = Array.isArray(this.secCategList)
          ? this.secCategList.filter(Boolean).join(', ')
          : (this.editData.secCateg || '')
      }
      this.saving = true
      this.apiRequest(`/product-imports/${this.importId}/rows/${this.editRowId}`, 'PUT', {
        token: this.$session.get('token'),
        data: this.editData
      }, (response) => {
        this.saving = false
        if (response && response.status) {
          this.editVisible = false
          this.reload()
        } else {
          swal({
            type: 'error',
            text: (response && response.data) || this.$t('general.error'),
            toast: true,
            position: 'top-end',
            timer: 3000,
            showConfirmButton: false
          })
        }
      })
    },
    removeRow (row) {
      swal({
        title: this.$t('productImport.removeConfirm'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('productImport.remove'),
        cancelButtonText: this.$t('general.cancel')
      }).then((result) => {
        if (result.value || result === true) {
          this.apiRequest(`/product-imports/${this.importId}/rows/${row.id}/remove`, 'POST', {
            token: this.$session.get('token')
          }, (response) => {
            if (response && response.status) {
              this.reload()
            }
          })
        }
      }).catch(() => {})
    },
    confirmBatch () {
      if (!this.confirmPriceProfileId) {
        swal({
          type: 'warning',
          text: this.$t('productImport.priceProfileRequired'),
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false
        })
        return
      }
      this.confirming = true
      const pendingRowIds = {}
      ;(this.rows || []).forEach((row) => {
        if (row && (row.status === 'PENDING' || row.status === 'ERROR')) {
          pendingRowIds[row.id] = true
        }
      })
      const doConfirm = () => {
        this.apiRequest(`/product-imports/${this.importId}/confirm`, 'POST', {
          token: this.$session.get('token')
        }, (response) => {
          this.confirming = false
          if (response && response.status) {
            this.confirmVisible = false
            const batch = response.data || {}
            this.trackAdminEvent('ADMIN_PRODUCT_IMPORT_CONFIRMED', 'product_import', this.importId, {
              product_import_id: this.importId,
              location_id: this.locationId,
              price_profiles_id: this.confirmPriceProfileId || batch.price_profiles_id || null,
              status: batch.status || null,
              total_rows: batch.total_rows != null ? batch.total_rows : null,
              confirmed_rows: batch.confirmed_rows != null ? batch.confirmed_rows : null,
              pending_rows: batch.pending_rows != null ? batch.pending_rows : null,
              error_rows: batch.error_rows != null ? batch.error_rows : null,
              removed_rows: batch.removed_rows != null ? batch.removed_rows : null,
              response: response
            })
            const confirmedRows = Array.isArray(batch.rows)
              ? batch.rows.filter((row) => (
                row &&
                row.status === 'CONFIRMED' &&
                row.product_id &&
                pendingRowIds[row.id]
              ))
              : []
            confirmedRows.forEach((row) => {
              const productId = row.product_id
              const action = row.action
              const eventName = action === 'CREATE'
                ? 'ADMIN_PRODUCT_CREATED'
                : 'ADMIN_PRODUCT_UPDATE'
              this.trackAdminEvent(eventName, 'product', productId, {
                product_id: productId,
                product_import_id: this.importId,
                location_id: this.locationId,
                import_action: action || null,
                import_row_id: row.id || null,
                name: (row.data && row.data.name) || null,
                price_profiles_id: this.confirmPriceProfileId || batch.price_profiles_id || null
              })
            })
            swal({
              type: 'success',
              text: this.$t('productImport.confirmSuccess'),
              toast: true,
              position: 'top-end',
              timer: 3000,
              showConfirmButton: false
            })
            this.reload()
          } else {
            swal({
              type: 'error',
              text: (response && response.data) || this.$t('general.error'),
              toast: true,
              position: 'top-end',
              timer: 4000,
              showConfirmButton: false
            })
          }
        })
      }

      if (this.batch && this.confirmPriceProfileId === this.batch.price_profiles_id) {
        doConfirm()
        return
      }

      this.apiRequest(`/product-imports/${this.importId}`, 'PUT', {
        token: this.$session.get('token'),
        price_profiles_id: this.confirmPriceProfileId
      }, (response) => {
        if (response && response.status) {
          this.batch = response.data
          doConfirm()
        } else {
          this.confirming = false
          swal({
            type: 'error',
            text: (response && response.data) || this.$t('general.error'),
            toast: true,
            position: 'top-end',
            timer: 3000,
            showConfirmButton: false
          })
        }
      })
    },
    cancelBatch () {
      this.apiRequest(`/product-imports/${this.importId}/cancel`, 'POST', {
        token: this.$session.get('token')
      }, (response) => {
        if (response && response.status) {
          this.reload()
        }
      })
    }
  }
}
</script>

<style scoped>
.import-detail-header {
  margin-bottom: 4px;
}

.import-detail-header__title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.import-detail-header__title h5 {
  margin: 0;
}

.import-status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  background: #E4E8F9;
  color: #674BD2;
}

.import-status--confirmed {
  background: #E6F7F2;
  color: #17B086;
}

.import-status--pending {
  background: #FFF4E5;
  color: #ff8e00;
}

.import-status--canceled {
  background: #FFEBEA;
  color: #FF0D19;
}

.import-status--partially_confirmed {
  background: #E4E8F9;
  color: #674BD2;
}

.import-detail-meta,
.import-detail-stats {
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.4;
}

.product-import-detail .data-table >>> .el-table {
  background-color: #fff;
}

.product-import-detail .data-table >>> .el-table .el-table__body-wrapper {
  background-color: #fff;
}

.product-import-detail .data-table >>> .el-table thead tr {
  background-color: #E4E8F9 !important;
  clip-path: none;
}

.product-import-detail .data-table >>> .el-table thead th {
  background-color: #E4E8F9 !important;
  border-bottom: none !important;
  padding: 8px 0 !important;
}

.product-import-detail .data-table >>> .el-table thead th:first-child {
  border-top-left-radius: 11px;
  border-bottom-left-radius: 11px;
}

.product-import-detail .data-table >>> .el-table thead th:last-child {
  border-top-right-radius: 11px;
  border-bottom-right-radius: 11px;
}

.product-import-detail .data-table >>> .el-table thead th > .cell {
  background: transparent !important;
  height: auto !important;
  min-height: 32px;
  line-height: 1.3;
}

.product-import-detail .data-table >>> .el-table tbody tr.el-table__row {
  background-color: #fff !important;
  clip-path: none;
}

.product-import-detail .data-table >>> .el-table tbody tr.el-table__row > td {
  background-color: #fff !important;
  border-bottom: 1px solid #E6EFFB !important;
  padding: 6px 0 !important;
}

.product-import-detail .data-table >>> .el-table tbody tr.el-table__row:last-child > td {
  border-bottom: none !important;
}

.product-import-detail .data-table >>> .el-table tbody tr.el-table__row > td > .cell {
  width: 100%;
  box-sizing: border-box;
  background: transparent !important;
  border-radius: 0 !important;
  height: auto !important;
  min-height: 40px;
  display: flex;
  align-items: center;
  line-height: 1.3;
  padding-top: 4px;
  padding-bottom: 4px;
  overflow: hidden;
}

.product-import-detail .data-table >>> .el-table tbody tr.el-table__row:hover > td {
  background-color: #E4E8F9 !important;
}

.product-import-detail .data-table >>> .el-table tbody tr.el-table__row:hover > td > .cell {
  background: transparent !important;
}

.import-row-actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  width: 100%;
}

.product-import-detail .data-table >>> .import-row-edit.edit {
  max-width: none !important;
  width: auto !important;
  min-width: 118px;
  margin: 0 !important;
  padding: 7px 18px !important;
  height: 39px;
  line-height: 27px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.product-import-dialog-btn {
  text-transform: none !important;
  font-weight: 400;
  border-radius: 50px;
  border: 1px solid transparent;
  box-sizing: border-box;
  padding: 7px 18px !important;
  height: 39px !important;
  line-height: 23px;
  width: auto !important;
  min-width: auto !important;
}

.product-import-dialog-btn.btn-info {
  background: #E4F4FF;
  color: #0097FC;
  border-color: transparent;
}

.product-import-dialog-btn.btn-info:hover,
.product-import-dialog-btn.btn-info:focus {
  background: #E4F4FF;
  color: #0097FC;
  border-color: #0097FC;
}

.product-import-dialog-btn.btn-success {
  background: #EBF8FA;
  color: #17B086;
  border-color: transparent;
}

.product-import-dialog-btn.btn-success:hover,
.product-import-dialog-btn.btn-success:focus {
  background: #EBF8FA;
  color: #17B086;
  border-color: #17B086;
}

.product-import-dialog-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.product-match-status {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.3;
}

.product-match-status.is-muted {
  color: #8a94a6;
}

.product-match-status.is-success {
  color: #17B086;
}

.product-match-status.is-error {
  color: #FF0D19;
}

.price-source-group {
  margin-bottom: 12px;
}

.price-source-group__title {
  font-size: 13px;
  font-weight: 600;
  color: #674BD2;
  margin-bottom: 8px;
}
</style>

<style>
/*
  Product Import dialogs open from inside GlobalModal (z-index 99999).
  Append-to-body mounts them on document.body, so they must sit above GlobalModal
  and be constrained to the content area (right of the sidebar).
*/
body.tt-import-dialog-open .v-modal {
  z-index: 1000000 !important;
  left: 240px !important;
  right: 0 !important;
  width: auto !important;
}

body.tt-import-dialog-open .el-dialog__wrapper {
  z-index: 1000001 !important;
  left: 240px !important;
  right: 0 !important;
  width: auto !important;
}

body.sidebar-mini.tt-import-dialog-open .v-modal,
body.sidebar-mini.tt-import-dialog-open .el-dialog__wrapper {
  left: 120px !important;
}

.el-dialog.product-import-edit-dialog {
  width: 92% !important;
  max-width: 1320px;
  margin-bottom: 4vh;
  overflow: visible;
}

.el-dialog.product-import-edit-dialog .el-dialog__body {
  max-height: 78vh;
  overflow-y: auto;
  padding-top: 10px;
  padding-bottom: 10px;
}

.el-dialog.product-import-edit-dialog .el-dialog__footer {
  padding: 12px 24px 20px !important;
  overflow: visible;
  text-align: right;
  box-sizing: border-box;
}

.el-dialog.product-import-edit-dialog .dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.el-dialog.product-import-edit-dialog h6 {
  margin: 12px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #3c4858;
}

.el-dialog.product-import-edit-dialog hr.sm {
  margin-top: 6px;
  margin-bottom: 12px;
}

.el-dialog.product-import-edit-dialog .product-import-dialog-btn {
  text-transform: none !important;
  font-weight: 400;
  border-radius: 50px;
  border: 1px solid transparent;
  box-sizing: border-box;
  padding: 7px 18px !important;
  height: 39px !important;
  line-height: 23px;
  flex: 0 0 auto;
  white-space: nowrap;
  overflow: visible;
  /* Global .btn-success is width/height: 46px (icon top-bar). Override for labeled footer actions. */
  width: auto !important;
  min-width: auto !important;
}

.el-dialog.product-import-edit-dialog .product-import-dialog-btn.btn-info {
  background: #E4F4FF;
  color: #0097FC;
  border-color: transparent;
}

.el-dialog.product-import-edit-dialog .product-import-dialog-btn.btn-info:hover,
.el-dialog.product-import-edit-dialog .product-import-dialog-btn.btn-info:focus {
  background: #E4F4FF;
  color: #0097FC;
  border-color: #0097FC;
}

.el-dialog.product-import-edit-dialog .product-import-dialog-btn.btn-success {
  background: #EBF8FA;
  color: #17B086;
  border-color: transparent;
}

.el-dialog.product-import-edit-dialog .product-import-dialog-btn.btn-success:hover,
.el-dialog.product-import-edit-dialog .product-import-dialog-btn.btn-success:focus {
  background: #EBF8FA;
  color: #17B086;
  border-color: #17B086;
}

.el-dialog.product-import-edit-dialog .product-import-dialog-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Settings/Locations/ProductImports/ProductImportDetail.vue